/**
 * Formats a number to a 2-digit string with leading zeros
 * @param {number|string} value - The value to format
 * @param {number} min - Minimum allowed value (default: 1)
 * @param {number} max - Maximum allowed value (default: 99)
 * @returns {string} Formatted string (e.g., 8 -> "08") or empty string if invalid
 */
export const formatToTwoDigits = (value, min = 1, max = 99) => {
  if (!value || value === "") return "";
  const num = parseInt(value);
  if (isNaN(num) || num < min || num > max) return "";
  return num.toString().padStart(2, "0");
};

/**
 * Formats a number to a 3-digit string with leading zeros
 * @param {number|string} value - The value to format
 * @param {number} min - Minimum allowed value (default: 1)
 * @param {number} max - Maximum allowed value (default: 999)
 * @returns {string} Formatted string (e.g., 8 -> "008") or empty string if invalid
 */
export const formatToThreeDigits = (value, min = 1, max = 999) => {
  if (!value || value === "") return "";
  const num = parseInt(value);
  if (isNaN(num) || num < min || num > max) return "";
  return num.toString().padStart(3, "0");
};

/**
 * Formats a number to a 4-digit string with leading zeros
 * @param {number|string} value - The value to format
 * @param {number} min - Minimum allowed value (default: 1)
 * @param {number} max - Maximum allowed value (default: 9999)
 * @returns {string} Formatted string (e.g., 8 -> "0008") or empty string if invalid
 */
export const formatToFourDigits = (value, min = 1, max = 9999) => {
  if (!value || value === "") return "";
  const num = parseInt(value);
  if (isNaN(num) || num < min || num > max) return "";
  return num.toString().padStart(4, "0");
};

/**
 * Parses a 3-digit formatted string to a plain number string
 * @param {string} value - The formatted value to parse (e.g., "008")
 * @returns {string} Plain number string (e.g., "8") or empty string if invalid
 */
export const parseThreeDigits = (value) => {
  if (!value) return "";
  const num = parseInt(value);
  return num > 0 ? num.toString() : "";
};

/**
 * Formats a number to output number format (e.g., 1 -> "D01")
 * @param {number|string} value - The value to format
 * @returns {string} Formatted output number or "000" if invalid
 */
export const formatOutputNumber = (value) => {
  if (!value || value === "") return "000";
  const num = parseInt(value);
  if (isNaN(num) || num < 1 || num > 96) return "000";
  return `D${num.toString().padStart(2, "0")}`;
};

/**
 * Parses an output number format to plain number (e.g., "D01" -> "1")
 * @param {string} value - The formatted output number
 * @returns {string} Plain number string or empty string if invalid
 */
export const parseOutputNumber = (value) => {
  if (!value || value === "000") return "";
  if (value.startsWith("D")) {
    const num = parseInt(value.substring(1));
    return num > 0 ? num.toString() : "";
  }
  return "";
};
